import React, { useEffect } from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardBody, CardFooter, Button, Divider, addToast } from "@heroui/react";
import { Icon } from "@iconify/react";
import { PersonalInfoForm } from "./PersonalInfoForm";
import { MedicalQuestionsForm } from "./MedicalQuestionsForm";
import { DosageForm } from "./DosageForm";
import { DocumentsUploadForm } from "./DocumentsUploadForm";
import { TransportersForm } from "./TransportersForm";
import { RequestSchema, TRequest, serviceLevelOf } from "@shared/entities";
import { useAppApi } from "src/appApi";
import { useParams } from "wouter";

type Step = {
	fields: Array<FieldPath<TRequest>>;
};
const steps: Array<Step> = [
	{ fields: ["clientDetails", "requestType", "reasonType", "userComment"] },
	{
		fields: ["medicalQuestionnaire"],
	},
	{
		fields: ["dosages", "existsDosage"],
	},
	{
		fields: ["documents"],
	},
	{
		fields: ["transporters"],
	},
] as const;

export default function AdminEditRequest() {
	const params = useParams();
	console.log("params", params);

	const requestId = params.id;

	const methods = useForm<TRequest>({
		resolver: zodResolver(RequestSchema),
		shouldUnregister: false,
		// mode: "onChange",
		defaultValues: {
			paymentDetails: {
				status: "pending",
				cost: 0,
				payments: [],
			},
		},
	});

	console.log("form", methods.watch());

	const appApi = useAppApi();

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = methods;

	const onSubmit = async (data: TRequest) => {
		try {
			// Only the price is the admin's to edit here. Payments and the payment
			// status belong to the server: writing back the copy this form loaded
			// would erase a payment recorded while it was open. The server
			// recomputes the status when the price changes.
			const { paymentDetails, ...rest } = data;
			await appApi.api.admin.setRequest({
				...rest,
				paymentDetails: { cost: paymentDetails?.cost || 0 } as TRequest["paymentDetails"],
			});
			appApi.navigate("/admin");
		} catch (error) {
			console.error("Error submitting form:", error);
			addToast({ title: "שמירת הבקשה נכשלה", description: (error as Error).message, color: "danger" });
		}
	};

	const [currentStep, setCurrentStep] = React.useState(1);
	const totalSteps = steps.length;

	const isLastStep = currentStep >= totalSteps;

	const nextStep = async () => {
		if (isLastStep) {
			// Use handleSubmit for final submission to trigger validation and error callback
			handleSubmit(onSubmit, (errors) => {
				appApi.api.system.uiLogs({
					message: "form error",
					severity: "ERROR",
					errors,
				});
			})();
			return;
		}
		const stepDetails = steps[currentStep - 1];
		console.log("stepDetails", stepDetails, currentStep);

		if (!stepDetails) return;

		const isValid = await methods.trigger(stepDetails.fields);
		console.log("isValid", isValid);

		if (!isValid) {
			return;
		}

		setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
	};

	const prevStep = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <PersonalInfoForm />;
			case 2:
				return <MedicalQuestionsForm />;
			case 3:
				return <DosageForm />;
			case 4:
				return <DocumentsUploadForm />;
			case 5:
				return <TransportersForm />;
			default:
				return <PersonalInfoForm />;
		}
	};

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return "פרטים אישיים";
			case 2:
				return "שאלון רפואי";
			case 3:
				return "מינון מבוקש";
			case 4:
				return "העלאת מסמכים";
			case 5:
				return "פרטי משנעים";
			default:
				return "";
		}
	};

	useEffect(() => {
		if (!requestId) return;

		appApi.api.admin.getRequest(requestId).then((res) => {
			console.log("res", res);
			if (res.success) {
				methods.reset({
					...res.data,
					serviceLevel: serviceLevelOf(res.data),
					paymentDetails: {
						status: res.data.paymentDetails?.status ?? "pending",
						cost: res.data.paymentDetails?.cost ?? 0,
						payments: res.data.paymentDetails?.payments ?? [],
					},
				});
			}
		});
	}, [requestId]);

	return (
		<div className="min-h-screen bg-gray-50 p-4 md:p-6">
			<div className="max-w-4xl mx-auto">
				<Card className="mb-6">
					<CardHeader className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold">יצירת בקשה חדשה</h1>
						<p className="text-default-500">מילוי פרטי בקשה לרישיון קנאביס רפואי</p>
					</CardHeader>
				</Card>

				<div className="mb-6">
					<div className="flex justify-between items-center mb-2">
						<h2 className="text-lg font-semibold">{getStepTitle()}</h2>
						<div className="text-sm text-default-500">
							שלב {currentStep} מתוך {totalSteps}
						</div>
					</div>
					<div className="w-full bg-default-100 rounded-full h-2.5">
						<div
							className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${(currentStep / totalSteps) * 100}%` }}
						></div>
					</div>
				</div>

				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit, (errors) => {
							console.log("errors", errors);
							appApi.api.system.uiLogs({
								message: "form error",
								severity: "ERROR",
								errors,
							});
						})}
					>
						<Card className="mb-6">
							<CardBody>{renderStepContent()}</CardBody>
							<Divider />
							<CardFooter className="flex justify-between">
								<Button
									variant="flat"
									color="default"
									startContent={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
									onPress={prevStep}
									isDisabled={currentStep === 1}
								>
									הקודם
								</Button>

								<Button
									color={isLastStep ? "success" : "primary"}
									type={"button"}
									isLoading={isSubmitting}
									onPress={nextStep}
									endContent={
										isLastStep ? (
											<Icon icon="lucide:check" className="h-4 w-4" />
										) : (
											<Icon icon="lucide:arrow-left" className="h-4 w-4" />
										)
									}
									// isDisabled={!isDirty || !isValid}
								>
									{isLastStep ? "שמירה" : "המשך"}
								</Button>
							</CardFooter>
						</Card>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}
